/**
 * Shared GlobalId → selection-target resolution.
 *
 * Both BCF viewpoints (viewpoint.applyViewpoint) and the FireBIM handoff
 * (FireBimHandoffReceiver.focusByGlobalIds) need to turn a set of IFC
 * GlobalIds into the concrete localIds to select/highlight, and both
 * must cope with GUIDs that point at entities with no geometry of their
 * own (IfcZone, IfcSystem, IfcGroup, a spatial storey).
 *
 * Fragments' getLocalIdsByGuids only maps entities it indexes (geometry
 * plus the spatial structure); a zone/system GUID comes back null. This
 * resolver falls back to the parallel rawIfc parser (guid → expressID
 * for the aggregation types) and, for any resolved entity that carries
 * no geometry, also adds its members (IfcRelAssignsToGroup) and contained
 * elements (IfcRelContainedInSpatialStructure) so the highlighter has
 * something geometric to paint. The grouping entity itself stays in the
 * set so the properties panel still surfaces it.
 *
 * Pure resolution only: callers decide what to do with the result
 * (select, force-visible, zoom). Single model per call; callers that
 * span a federation loop over their loaded models.
 */
import { viewerApi } from './viewerApi'
import { rawIfc } from './rawIfc'

export async function resolveGuidTargets(
  modelId: string,
  globalIds: string[],
): Promise<number[]> {
  if (globalIds.length === 0) return []
  const model = viewerApi.getModel(modelId)
  if (!model) return []

  // 1. Fragments guid → localId (covers geometry + spatial structure).
  //    Null entries are the ones Fragments doesn't index.
  let fragMapped: (number | null)[] = []
  try {
    fragMapped = await model.getLocalIdsByGuids(globalIds)
  } catch (err) {
    console.warn('[resolveGuidTargets] getLocalIdsByGuids failed', err)
  }

  const hasRaw = rawIfc.hasModel(modelId)
  const entityIds: number[] = []
  globalIds.forEach((guid, i) => {
    const lid = fragMapped[i]
    if (typeof lid === 'number') {
      entityIds.push(lid)
    } else if (hasRaw) {
      // 2. Fallback: aggregation entities (zone/system/group) that
      //    Fragments leaves out of its guid index.
      const eid = rawIfc.getLocalIdByGuid(modelId, guid)
      if (eid != null) entityIds.push(eid)
    }
  })
  if (entityIds.length === 0) return []

  // 3. Which of those actually carry geometry? The rest need expanding
  //    to their members so the selection is visible in 3D.
  let geometryIds = new Set<number>()
  try {
    geometryIds = new Set(await model.getItemsIdsWithGeometry())
  } catch (err) {
    console.debug('[resolveGuidTargets] getItemsIdsWithGeometry failed', err)
  }

  const out = new Set<number>()
  for (const id of entityIds) {
    out.add(id)
    if (!geometryIds.has(id) && hasRaw) {
      // Group/system members (RelAssignsToGroup) and spatial containment
      // (RelContainedInSpatialStructure). One or both are empty for a
      // given entity; the union covers zones, systems and storeys.
      for (const m of rawIfc.getGroupMembers(modelId, id)) out.add(m)
      for (const m of rawIfc.getContainedElements(modelId, id)) out.add(m)
    }
  }
  return [...out]
}
